import { useMutation, UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import {
  ResponseError,
  SaveFieldMappingRequest,
  SaveFieldMappingResponse,
} from "lib/generated/integration-api";
import { useIntegrationApi } from "lib/integrationApiContext";
import { FIELD_MAPPINGS_QUERY_KEY } from "./useFieldMappings";

/**
 * The service answers a mapping it will not store with 400 and a message naming the field at fault.
 * The generated client throws its own error type carrying the raw response, so the message has to
 * be read back out of it rather than off `Error.message`, which is always "Response returned an
 * error code".
 */
export async function rejectionReason(error: unknown): Promise<string | undefined> {
  if (!(error instanceof ResponseError) || error.response.status !== 400) return undefined;
  try {
    const body: unknown = await error.response.clone().json();
    const message = (body as { message?: unknown }).message;
    return typeof message === "string" && message ? message : undefined;
  } catch {
    return undefined;
  }
}

export function useSaveFieldMapping(
  options?: UseMutationOptions<SaveFieldMappingResponse, unknown, SaveFieldMappingRequest>,
) {
  const api = useIntegrationApi();
  const queryClient = useQueryClient();

  return useMutation(
    (request: SaveFieldMappingRequest) =>
      api.saveFieldMapping({ saveFieldMappingRequest: request }),
    {
      ...options,
      onSuccess: async (data, request, context) => {
        // The saved mapping is what the form reads back on a reload, and the service settles what
        // was stored — the object name and a lookup's targets both come back resolved.
        await queryClient.invalidateQueries(FIELD_MAPPINGS_QUERY_KEY);
        await options?.onSuccess?.(data, request, context);
      },
    },
  );
}
